import {
  addMonthsToDate,
  calcValidTill,
  cleanPhone,
  COMBO_TYPES,
  normaliseCourse,
  normaliseProgram,
  normalizeCheckoutPhone,
  normalizeDialCode,
  validateStudentPhonePk,
} from '@fintree/utils'
import type { DbClient } from '@fintree/supabase/db-client'
import { defaultDbClient } from '@fintree/supabase/db-client'
import { logActivity, type ActivityActor, withActivityActor } from '../internal/activity'
import { createInstallment } from '../installments/writes'
import type {
  CreateEnrollmentPayload,
  CreateExtensionPayload,
  EnrollmentInsert,
  EnrollmentRow,
  ExtensionInsert,
  ExtensionRow,
  StudentInsert,
  StudentRow,
} from './dto'

function toEnrollmentWriteError(error: unknown): Error {
  const fallback = new Error('Unable to create enrollment')
  if (!error || typeof error !== 'object') return fallback

  const typedError = error as {
    status?: number
    code?: string
    message?: string
    details?: string
    hint?: string
  }

  const status = typedError.status
  const code = typedError.code ?? ''
  const message = typedError.message ?? ''
  const details = typedError.details ?? ''
  const text = `${message} ${details} ${typedError.hint ?? ''}`.toLowerCase()
  const isPermissionError =
    status === 403 ||
    code === '42501' ||
    text.includes('permission denied') ||
    text.includes('row-level security') ||
    text.includes('http status code 403') ||
    text.includes('status 403')

  if (isPermissionError) return new Error('Database permission error — contact super_admin')

  const isUniqueViolation = code === '23505' || text.includes('duplicate key value violates unique constraint')
  if (isUniqueViolation) {
    if (text.includes('idx_enrollments_unique_active')) {
      return new Error(
        'An active enrollment already exists for this course, attempt, and package. ' +
          'Use the Enrollment Change tab to extend validity, retake, or upgrade package.'
      )
    }
    return new Error(`Duplicate value: ${message} ${details}`.trim())
  }

  if (error instanceof Error) return error
  if (typedError.message) return new Error(typedError.message)
  return fallback
}

async function assertNoDuplicateActiveEnrollment(
  phone: string,
  course: string,
  attempt: string | null,
  program: string | null,
  client: DbClient = defaultDbClient
): Promise<void> {
  const attemptValue = attempt?.trim() || null
  const programValue = program?.trim() || null

  let query = client
    .from('enrollments')
    .select('id')
    .eq('phone', phone)
    .eq('course', course)
    .is('deleted_at', null)

  if (attemptValue) {
    query = query.eq('attempt', attemptValue)
  } else {
    query = query.or('attempt.is.null,attempt.eq.')
  }

  if (programValue) {
    query = query.eq('program', programValue)
  } else {
    query = query.or('program.is.null,program.eq.')
  }

  const { data: existingRows, error } = await query.limit(1)

  if (error) {
    throw toEnrollmentWriteError(error)
  }

  if (existingRows?.length) {
    throw new Error(
      'An active enrollment already exists for this course, attempt, and package. ' +
        'Use the Enrollment Change tab to extend validity, retake, or upgrade package.'
    )
  }
}

function resolvePhone(payload: CreateEnrollmentPayload): { phone: string; countryCode: string | null } {
  const currency = payload.currency?.trim().toUpperCase() || (payload.country ? 'USD' : 'INR')
  const result = normalizeCheckoutPhone({
    rawPhone: payload.phone,
    addressCountry: payload.country ?? (currency === 'INR' ? 'India' : null),
    addressCountryCode: payload.countryCode ?? null,
    currency,
  })
  if (result.ok) {
    return { phone: result.phone, countryCode: result.countryCode }
  }
  if (payload.country || payload.phone.startsWith('+')) {
    const cc = normalizeDialCode(payload.countryCode ?? '')
    return { phone: payload.phone.replace(/\D/g, ''), countryCode: cc || null }
  }
  const cleaned = cleanPhone(payload.phone)
  return { phone: cleaned || payload.phone.trim(), countryCode: '91' }
}

export async function createEnrollment(
  payload: CreateEnrollmentPayload,
  actor: ActivityActor,
  client: DbClient = defaultDbClient
): Promise<EnrollmentRow> {
  const currency = (payload.currency?.trim().toUpperCase() || (payload.country ? 'USD' : 'INR')) as string
  const { phone, countryCode } = resolvePhone({ ...payload, currency })
  if (!validateStudentPhonePk(phone)) {
    throw new Error('Phone must be 10–15 digits (digits only, no symbols).')
  }
  const combo = payload.comboType ? COMBO_TYPES[payload.comboType] : null
  const rawCourse = combo?.startCourse ?? payload.course
  const studentCourse = (normaliseCourse(rawCourse) || rawCourse).trim()
  const programNorm =
    payload.package != null && String(payload.package).trim() !== ''
      ? normaliseProgram(String(payload.package))
      : null
  const validTill = calcValidTill(
    studentCourse,
    payload.attempt,
    payload.comboType,
    payload.enrollmentDate,
    payload.revisionMonths
  )
  const status = payload.status ?? 'active'
  const source = payload.source ?? 'hub'

  const { data: existingStudent } = await client
    .from('students')
    .select('phone, deleted_at')
    .eq('phone', phone)
    .maybeSingle()
  if (existingStudent?.deleted_at) {
    throw new Error('A deleted student exists with this phone. Restore them first or use a different phone.')
  }

  await assertNoDuplicateActiveEnrollment(phone, studentCourse, payload.attempt ?? null, programNorm, client)

  const studentUpsert: StudentInsert = {
    phone,
    name: payload.name ?? null,
    email: payload.email ?? null,
    city: payload.city ?? null,
    country: payload.country ?? null,
    country_code: countryCode ?? (payload.countryCode ? normalizeDialCode(payload.countryCode) : null),
    course: studentCourse,
    program: programNorm,
    attempt: payload.attempt ?? null,
    valid_till: validTill,
    amount: payload.amount ?? null,
    currency,
    gateway: payload.paymentMode ?? null,
    razorpay_payment_id: payload.razorpayPaymentId ?? null,
    enrollment_date: payload.enrollmentDate,
    combo_package: payload.comboType ? true : null,
    combo_levels: payload.comboType ?? null,
    source,
    // Invariant #9: do not set students.status together with valid_till;
    // the auto_status_from_valid_till trigger derives it from valid_till.
  }

  const { error: upsertError } = await client.from('students').upsert(studentUpsert, { onConflict: 'phone' })
  if (upsertError) throw toEnrollmentWriteError(upsertError)

  const enrollmentInsert: EnrollmentInsert = {
    phone,
    course: studentCourse,
    program: programNorm,
    attempt: payload.attempt ?? null,
    valid_till: validTill,
    amount: payload.amount ?? null,
    currency,
    gateway: payload.paymentMode ?? null,
    razorpay_payment_id: payload.razorpayPaymentId ?? null,
    enrollment_date: payload.enrollmentDate,
    combo_package: payload.comboType ? true : null,
    combo_levels: payload.comboType ?? null,
    status,
    source,
    revision_months: payload.revisionMonths ?? null,
    notes: payload.notes ?? null,
  }

  const { data, error } = await client.from('enrollments').insert(enrollmentInsert).select('*').single()
  if (error) throw toEnrollmentWriteError(error)

  if ((payload.amount ?? 0) > 0) {
    const { error: paymentError } = await client.from('payments').insert({
      phone,
      enrollment_id: data.id,
      amount: payload.amount as number,
      currency,
      received_at: payload.enrollmentDate ?? new Date().toISOString(),
      source: 'hub',
      gateway: payload.paymentMode ?? null,
      reference_id: payload.razorpayPaymentId ?? null,
      notes: `Payment recorded during enrollment creation (${studentCourse})`,
    })
    if (paymentError) {
      console.error('Payment insert failed during enrollment creation:', paymentError)
    }
  }

  if (payload.installments && payload.installments.length > 0) {
    for (const inst of payload.installments) {
      try {
        await createInstallment(
          {
            phone,
            enrollmentId: data.id,
            amount: inst.amount,
            dueDate: inst.dueDate,
            notes: inst.notes ?? null,
          },
          actor,
          client
        )
      } catch (installmentError) {
        console.error('Installment insert failed during enrollment creation:', installmentError)
      }
    }
  }

  await logActivity(
    withActivityActor(actor, {
      action: 'enrollment.create',
      entityType: 'enrollments',
      entityId: phone,
      newValues: data,
    }),
    client
  )

  return data
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function computeUpdatedStudent(student: StudentRow, payload: CreateExtensionPayload): {
  attempt: string | null
  program: string | null
  validTill: string | null
} {
  if (payload.type === 'attempt_extension' || payload.type === 'retake') {
    const attempt = payload.newAttempt ?? student.attempt
    return {
      attempt: attempt ?? null,
      program: student.program,
      validTill: calcValidTill(student.course ?? '', attempt ?? undefined, student.combo_levels ?? undefined, payload.enrollmentDate),
    }
  }

  if (payload.type === 'package_upgrade') {
    const nextProgram =
      payload.newPackage != null && String(payload.newPackage).trim() !== ''
        ? normaliseProgram(String(payload.newPackage))
        : student.program
    return {
      attempt: student.attempt,
      program: nextProgram,
      validTill: student.valid_till,
    }
  }

  const baseDate = student.valid_till ?? payload.enrollmentDate ?? todayIso()
  return {
    attempt: student.attempt,
    program: student.program,
    validTill: payload.durationMonths ? addMonthsToDate(baseDate, payload.durationMonths) : student.valid_till,
  }
}

export async function createExtension(
  payload: CreateExtensionPayload,
  actor: ActivityActor,
  client: DbClient = defaultDbClient
): Promise<ExtensionRow> {
  if (!validateStudentPhonePk(payload.phone)) {
    throw new Error('Phone must be 10–15 digits (digits only, no symbols).')
  }

  const { data: student, error: studentError } = await client
    .from('students')
    .select('*')
    .eq('phone', payload.phone)
    .is('deleted_at', null)
    .maybeSingle()
  if (studentError) throw studentError
  if (!student) throw new Error('Student not found or has been deleted.')

  const updated = computeUpdatedStudent(student, payload)
  const extensionInsert: ExtensionInsert = {
    phone: payload.phone,
    type: payload.type,
    old_course: student.course,
    old_attempt: student.attempt,
    old_package: student.program,
    new_attempt: updated.attempt,
    new_package: updated.program,
    duration_added: payload.durationMonths != null ? String(payload.durationMonths) : null,
    amount_paid: payload.amountPaid,
    payment_mode: payload.paymentMode,
    razorpay_payment_id: payload.razorpayPaymentId ?? null,
    notes: payload.notes ?? null,
  }

  const { data, error } = await client.from('extensions').insert(extensionInsert).select('*').single()
  if (error) throw error

  let newEnrollmentId: string | null = null
  const shouldCreateEnrollment = payload.type === 'attempt_extension' || payload.type === 'retake'
  if (shouldCreateEnrollment) {
    const enrollmentInsert: EnrollmentInsert = {
      phone: payload.phone,
      course: student.course as string,
      program: updated.program,
      attempt: updated.attempt,
      valid_till: updated.validTill,
      amount: payload.amountPaid,
      gateway: payload.paymentMode,
      razorpay_payment_id: payload.razorpayPaymentId ?? null,
      enrollment_date: payload.enrollmentDate ?? todayIso(),
      combo_package: student.combo_package,
      combo_levels: student.combo_levels,
      status: 'active',
      source: 'hub_extension',
      is_retake: payload.type === 'retake',
      notes: payload.notes ?? null,
    }

    const { data: newEnrollment, error: enrollmentError } = await client
      .from('enrollments')
      .insert(enrollmentInsert)
      .select('id')
      .single()
    if (enrollmentError) throw enrollmentError
    newEnrollmentId = newEnrollment?.id ?? null
  }

  if ((payload.amountPaid ?? 0) > 0) {
    const { error: paymentError } = await client.from('payments').insert({
      phone: payload.phone,
      enrollment_id: newEnrollmentId,
      amount: payload.amountPaid as number,
      currency: student.currency,
      received_at: payload.enrollmentDate ?? new Date().toISOString(),
      source: 'extension',
      gateway: payload.paymentMode ?? null,
      reference_id: payload.razorpayPaymentId ?? null,
      notes: `Payment recorded during extension (${payload.type})`,
    })
    if (paymentError) {
      console.error('Payment insert failed during extension:', paymentError)
    }
  }

  const { data: updatedStudent, error: updateError } = await client
    .from('students')
    .update({
      attempt: updated.attempt,
      program: updated.program,
      valid_till: updated.validTill,
      extension_count: (student.extension_count ?? 0) + 1,
      last_extended_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('phone', payload.phone)
    .select('*')
    .single()
  if (updateError) throw updateError

  await logActivity(
    withActivityActor(actor, {
      action: 'extension.create',
      entityType: 'extensions',
      entityId: data.phone,
      oldValues: student,
      newValues: {
        extension: data,
        student: updatedStudent,
      },
    }),
    client
  )

  return data
}
